import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { TOUR_EXPAND_MENU_EVENT } from "../config/onboardingTourConfig";
import { ADVANCED_WELCOME_TOUR_STEPS } from "../config/advancedWelcomeTourConfig";
import { markAdvancedWelcomeTourComplete } from "../utils/advancedWelcomeTourStorage";

const AdvancedWelcomeTourContext = createContext(null);

function isElementVisible(el) {
  if (!el) return false;
  const rect = el.getBoundingClientRect();
  return rect.width > 1 && rect.height > 1;
}

function waitForElement(selector, timeout = 7000) {
  if (!selector) return Promise.resolve(null);

  return new Promise((resolve) => {
    const findVisible = () => {
      const found = document.querySelector(selector);
      return isElementVisible(found) ? found : null;
    };

    const existing = findVisible();
    if (existing) {
      resolve(existing);
      return;
    }

    const observer = new MutationObserver(() => {
      const found = findVisible();
      if (found) {
        cleanup();
        resolve(found);
      }
    });

    const interval = setInterval(() => {
      const found = findVisible();
      if (found) {
        cleanup();
        resolve(found);
      }
    }, 80);

    const cleanup = () => {
      observer.disconnect();
      clearInterval(interval);
      clearTimeout(timer);
    };

    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["class", "style"],
    });

    const timer = setTimeout(() => {
      cleanup();
      resolve(findVisible() || document.querySelector(selector));
    }, timeout);
  });
}

function getTargetRect(target) {
  if (!target || !isElementVisible(target)) return null;
  const rect = target.getBoundingClientRect();
  const pad = 10;
  return {
    top: rect.top - pad,
    left: rect.left - pad,
    width: rect.width + pad * 2,
    height: rect.height + pad * 2,
    centerX: rect.left + rect.width / 2,
    centerY: rect.top + rect.height / 2,
  };
}

function expandTourMenu(menuKey) {
  if (!menuKey) return;
  window.dispatchEvent(
    new CustomEvent(TOUR_EXPAND_MENU_EVENT, {
      detail: { menuKey },
    })
  );
}

export function AdvancedWelcomeTourProvider({ children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [isActive, setIsActive] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);
  const [targetRect, setTargetRect] = useState(null);
  const [isTransitioning, setIsTransitioning] = useState(false);

  const activeStep = isActive ? ADVANCED_WELCOME_TOUR_STEPS[stepIndex] || null : null;
  const totalSteps = ADVANCED_WELCOME_TOUR_STEPS.length;

  const updateTargetRect = useCallback(async (step) => {
    if (!step?.target) {
      setTargetRect(null);
      return;
    }
    const el = await waitForElement(step.target);
    if (el) {
      el.scrollIntoView({ block: "nearest", inline: "nearest" });
      // Layout can shift after scroll; measure on next frame
      await new Promise((resolve) => requestAnimationFrame(() => resolve()));
    }
    setTargetRect(getTargetRect(el));
  }, []);

  const goToStep = useCallback(
    async (index) => {
      const step = ADVANCED_WELCOME_TOUR_STEPS[index];
      if (!step) return;

      setIsTransitioning(true);
      setTargetRect(null);
      setStepIndex(index);

      const normalizedRoute = step.route.startsWith("/") ? step.route : `/${step.route}`;
      if (location.pathname !== normalizedRoute) {
        navigate(normalizedRoute);
        // Let route + SideBar path sync settle before expanding menus
        await new Promise((resolve) => setTimeout(resolve, 420));
      }

      if (step.expandMenuKey) {
        // Path sync on /dashboard clears open menus — expand after that, then again once Collapse opens
        expandTourMenu(step.expandMenuKey);
        await new Promise((resolve) => setTimeout(resolve, 80));
        expandTourMenu(step.expandMenuKey);
        await new Promise((resolve) => setTimeout(resolve, 420));
      } else {
        await new Promise((resolve) => setTimeout(resolve, 220));
      }

      await updateTargetRect(step);
      setIsTransitioning(false);
    },
    [location.pathname, navigate, updateTargetRect]
  );

  const startTour = useCallback(async () => {
    setIsActive(true);
    await goToStep(0);
  }, [goToStep]);

  const stopTour = useCallback(() => {
    setIsActive(false);
    setStepIndex(0);
    setTargetRect(null);
    setIsTransitioning(false);
  }, []);

  const nextStep = useCallback(async () => {
    if (!activeStep) return;

    const isLast = stepIndex >= totalSteps - 1;
    if (isLast) {
      markAdvancedWelcomeTourComplete();
      stopTour();
      window.dispatchEvent(new CustomEvent("advanced-welcome-tour:complete"));
      return;
    }

    await goToStep(stepIndex + 1);
  }, [activeStep, goToStep, stepIndex, stopTour, totalSteps]);

  const prevStep = useCallback(async () => {
    if (stepIndex <= 0) return;
    await goToStep(stepIndex - 1);
  }, [goToStep, stepIndex]);

  const skipTour = useCallback(() => {
    stopTour();
    window.dispatchEvent(new CustomEvent("advanced-welcome-tour:skipped"));
  }, [stopTour]);

  useEffect(() => {
    if (!isActive || !activeStep) return undefined;

    const handleLayoutChange = () => {
      void updateTargetRect(activeStep);
    };

    window.addEventListener("resize", handleLayoutChange);
    window.addEventListener("scroll", handleLayoutChange, true);
    return () => {
      window.removeEventListener("resize", handleLayoutChange);
      window.removeEventListener("scroll", handleLayoutChange, true);
    };
  }, [activeStep, isActive, updateTargetRect]);

  useEffect(() => {
    if (!isActive || !activeStep) return;
    void updateTargetRect(activeStep);
  }, [location.pathname, isActive, activeStep, updateTargetRect]);

  const value = useMemo(
    () => ({
      isActive,
      isTransitioning,
      activeStep,
      stepIndex,
      totalSteps,
      targetRect,
      startTour,
      nextStep,
      prevStep,
      skipTour,
      stopTour,
    }),
    [
      isActive,
      isTransitioning,
      activeStep,
      stepIndex,
      totalSteps,
      targetRect,
      startTour,
      nextStep,
      prevStep,
      skipTour,
      stopTour,
    ]
  );

  return (
    <AdvancedWelcomeTourContext.Provider value={value}>
      {children}
    </AdvancedWelcomeTourContext.Provider>
  );
}

export function useAdvancedWelcomeTour() {
  const context = useContext(AdvancedWelcomeTourContext);
  if (!context) {
    throw new Error("useAdvancedWelcomeTour must be used within AdvancedWelcomeTourProvider");
  }
  return context;
}

export function useAdvancedWelcomeTourOptional() {
  return useContext(AdvancedWelcomeTourContext);
}
